// Availability, cross-tagging, area-based KPIs — all driven by the seeded KB.
import { quantifyResource, DEFAULT_DIVERT_PCT } from "./resources";
import { annualOperatingHours, defaultProfileFor } from "./operatingProfile";

// Thermal-exchange recovery methods (Ventilation Exhaust Air). Only EXPORTABLE methods can supply
// the agriculture energy loop — a plate HX / thermal wheel / enthalpy wheel only preheats incoming
// outdoor air, so it contributes 0 kWh to agriculture until a secondary export loop is added.
export const RECOVERY_METHODS = [
  { id: "plate",         label: "Sensible plate heat exchanger", sensible: 0.65, latent: 0,    exportable: false, dest: "Incoming outdoor air",                note: "Transfers temperature through fixed plates to incoming OA — recovers to ventilation air only." },
  { id: "thermal_wheel", label: "Sensible thermal wheel",        sensible: 0.75, latent: 0,    exportable: false, dest: "Incoming outdoor air",                note: "Rotating wheel transfers temperature to incoming OA — ventilation air only." },
  { id: "enthalpy_wheel",label: "Enthalpy wheel",                sensible: 0.75, latent: 0.70, exportable: false, dest: "Incoming outdoor air",                note: "Transfers temperature + moisture to incoming OA — ventilation air only." },
  { id: "run_around",    label: "Run-around coil",               sensible: 0.55, latent: 0,    exportable: true,  dest: "Incoming OA or a separate liquid loop", note: "Two coils + a pumped water/glycol loop — can be routed to a separate loop and exported." },
  { id: "exhaust_hp",    label: "Exhaust-air heat pump",         sensible: 0.60, latent: 0,    exportable: true,  dest: "Hot-water loop / greenhouse / DHW",     note: "Extracts and UPGRADES low-temperature exhaust heat — exportable to agriculture." },
];

// Condenser-type presets for Cooling System Condenser Heat — sets a SUGGESTED recovery % (editable).
export const CONDENSER_TYPES = [
  { id: "air",   label: "Air-cooled",   recovery: 0.20, note: "Air-cooled condenser — lower-grade heat, smaller recoverable fraction." },
  { id: "water", label: "Water-cooled", recovery: 0.50, note: "Water-cooled condenser — higher-grade heat via the condenser-water loop, larger recoverable fraction." },
];

// Is this a condenser-type thermal loop (Cooling System Condenser Heat / Refrigerator) vs a
// ventilation-exhaust loop? Drives which config + effectiveness model applies.
export const isCondenserLoop = (r) => /hvac|condenser|cooling|refriger/i.test(`${r.id || ""} ${r.label || ""} ${r.tech || ""}`);

// Recovered kWh/yr a single locked thermal loop supplies to agriculture (0 if the method only
// preheats ventilation air). effRecovery = availableKw × effectiveness × annual operating hours.
export function thermalRecovery(resource, cfg = {}, country = "", typologyId = null) {
  const kw = Math.max(0, Number(cfg.availableKw) || 0);
  const hasOverride = cfg.hoursOverride != null && cfg.hoursOverride !== "";
  const hours = hasOverride
    ? Math.max(0, Math.min(8760, Number(cfg.hoursOverride) || 0))
    : annualOperatingHours(cfg.profileId || defaultProfileFor(typologyId), country, cfg.custom);
  if (isCondenserLoop(resource)) {
    const ct = CONDENSER_TYPES.find((c) => c.id === (cfg.condenserType || "air")) || CONDENSER_TYPES[0];
    const eff = cfg.recoveryEff != null && cfg.recoveryEff !== "" ? (Number(cfg.recoveryEff) || 0) / 100 : ct.recovery;
    const gross = Math.round(kw * eff * hours);
    return { kw, hours, eff, exportable: true, recoveredKwh: gross, recoveredGrossKwh: gross };
  }
  const m = RECOVERY_METHODS.find((x) => x.id === (cfg.method || "plate")) || RECOVERY_METHODS[0];
  const eff = cfg.sensibleEff != null && cfg.sensibleEff !== "" ? (Number(cfg.sensibleEff) || 0) / 100 : m.sensible;
  // Gross heat the exchanger recovers (always real, non-zero). Only EXPORTABLE methods route it to
  // agriculture; non-exportable methods still recover it — but to incoming ventilation air (a genuine
  // building heating saving), so 0 kWh is exportable to the growing system.
  const gross = Math.round(kw * eff * hours);
  return { kw, hours, eff, exportable: m.exportable, dest: m.dest, recoveredKwh: m.exportable ? gross : 0, recoveredGrossKwh: gross };
}


// Building typologies (mirrors backend BUILDING_TYPOLOGIES) — used for the sidebar dropdown
// and the per-capita food-coverage / waste-baseline readouts.
// organicKgPersonYr = annual food/organic-waste GENERATION per occupant, grounded per typology
// (UNEP Food Waste Index 2024: household 79, food-service 36; US EPA/UN 2024 US household 73;
// EPA 2018 Wasted Food Report: schools ≈ 12 kg/student·yr). Drives BOTH the Resources-tab
// valorization feedstock and the Building-Config "kg/day organics" readout (single source).
export const TYPOLOGIES = [
  { id: "residential", label: "Multi-Family Residential", perCapitaKgYr: 55, greywaterLpd: 95, organicKgPersonYr: 87.6, organicRef: "RES_Generation RG02 (v6, 0.24 kg/person·d)" },
  { id: "office", label: "Commercial Office", perCapitaKgYr: 18, greywaterLpd: 30, organicKgPersonYr: 36, organicRef: "UNEP Food Waste Index 2024" },
  { id: "mixed_use", label: "Mixed-Use", perCapitaKgYr: 35, greywaterLpd: 60, organicKgPersonYr: 55, organicRef: "UNEP Food Waste Index 2024" },
  { id: "educational", label: "Educational", perCapitaKgYr: 25, greywaterLpd: 40, organicKgPersonYr: 12, organicRef: "EPA Wasted Food Report 2018 (schools)" },
  { id: "healthcare", label: "Healthcare", perCapitaKgYr: 30, greywaterLpd: 120, organicKgPersonYr: 79, organicRef: "UNEP Food Waste Index 2024" },
];

// Annual organic-waste generation per occupant for a typology (kg/person·yr). Falls back to the
// EPA/UN US-household figure (73) when no typology is set yet.
export function organicKgPersonYr(typologyId) {
  const t = TYPOLOGIES.find((x) => x.id === typologyId);
  return t ? t.organicKgPersonYr : 87.6;
}

// Per-capita coverage + waste baselines from typology, occupancy and the scenario's annual yield.
export function occupancyBaselines(typologyId, occupancy, annualYieldKg) {
  const t = TYPOLOGIES.find((x) => x.id === typologyId);
  if (!t || !occupancy) return null;
  const need = occupancy * t.perCapitaKgYr;
  return {
    perCapitaKgYr: t.perCapitaKgYr,
    annualFoodNeedKg: Math.round(need),
    coveragePct: need > 0 ? Math.min(999, Math.round((annualYieldKg / need) * 100)) : 0,
    greywaterLpd: Math.round(occupancy * t.greywaterLpd),
    organicWasteKgDay: +((occupancy * t.organicKgPersonYr) / 365).toFixed(1),
  };
}

export function locationLabel(kb, id) {
  return kb?.locations.find((l) => l.id === id)?.short || id;
}
export function systemLabel(kb, id) {
  return kb?.systems.find((s) => s.id === id)?.label || id;
}

export function systemsFor(kb, locationIds) {
  const systems = kb?.systems || [];
  const ids = locationIds || [];
  // Phase 3 — soft (non-destructive): return ALL systems always; `tags` carry the matched
  // location labels. Compatibility is surfaced via relationalTags (Recommended / dim), not by hiding.
  return systems.map((s) => ({
    ...s,
    tags: ids.length ? s.locations.filter((l) => ids.includes(l)).map((l) => locationLabel(kb, l)) : [],
  }));
}

export function cropsFor(kb, systemIds) {
  const crops = kb?.crops || [];
  const systems = (kb?.systems || []).filter((s) => systemIds.includes(s.id));
  // Phase 3 — soft: return ALL crops always; `tags` carry the compatible-system labels.
  return crops.map((c) => ({
    ...c,
    tags: systems.filter((s) => systemHostsCrop(s, c)).map((s) => s.label),
  }));
}

// v5 — a configuration hosts a crop when the crop has data for the config's host class AND
// (if the config carries an explicit compatible_crops allowlist) the cultivar is on that list.
// Falls back to pure class matching when no allowlist is present (legacy data).
export function systemHostsCrop(system, crop) {
  if (!system || !crop || !crop.systems) return false;
  const classMatch = (system.db_systems || []).some((lb) => crop.systems[lb]);
  if (!classMatch) return false;
  const allow = system.compatible_crops;
  if (Array.isArray(allow) && allow.length > 0) return allow.includes(crop.cultivar);
  return true;
}

// The crop-DB class key a system uses to grow a crop (honours the allowlist); null if none.
export function cropHostKey(system, crop) {
  if (!systemHostsCrop(system, crop)) return null;
  return (system.db_systems || []).find((lb) => crop.systems[lb]) || null;
}

export function cropGroups(kb, systemIds) {
  const items = cropsFor(kb, systemIds);
  const groups = {};
  items.forEach((c) => {
    (groups[c.group] = groups[c.group] || []).push(c);
  });
  return groups;
}

// PHASE 3 — Relational Mesh. Bidirectional, cross-ring compatibility tagging that applies
// GLOBALLY (every ring, regardless of which ring is currently the root). Non-destructive:
// options are never hidden — compatible ones are flagged `recommended`, and options that
// conflict with an ACTIVE cross-ring selection are `dim`med (still selectable). Chain:
// Locations ⇄ Systems ⇄ Crops ⇄ Resources.
export function relationalTags(kb, selections) {
  const t = { locations: {}, systems: {}, crops: {}, resources: {} };
  if (!kb) return t;
  const sel = selections || { locations: [], systems: [], crops: [], resources: [] };
  // Status rule: PENDING rows never enter calculations (shown greyed + "data pending" in the UI).
  const selSys = (kb.systems || []).filter((s) => sel.systems.includes(s.id) && !s.data_pending);
  const selCrops = (kb.crops || []).filter((c) => sel.crops.includes(c.id) && !c.data_pending);
  let selRes = (kb.resources || []).filter((r) => sel.resources.includes(r.id) && !r.data_pending);
  // Exclusive groups (e.g. AD biogas vs AD-CHP flue-gas CO₂) draw on the same input — credit one only.
  const _seenGroups = new Set();
  selRes = selRes.filter((r) => {
    const g = r.exclusive_group;
    if (!g) return true;
    if (_seenGroups.has(g)) return false;
    _seenGroups.add(g);
    return true;
  });
  const selLoc = sel.locations || [];
  const anyLoc = selLoc.length, anySys = selSys.length, anyCrop = selCrops.length, anyRes = selRes.length;

  const hasWaterLoop = selRes.some((r) => r.category === "Water" || /water/i.test(r.output || ""));
  const hasAmendmentLoop = selRes.some((r) => r.category === "Amendment" || /compost|fertil|nutrient/i.test(r.output || ""));

  // LOCATIONS ⇐ Systems (reverse): recommended when a selected system anchors here.
  (kb.locations || []).forEach((l) => {
    if (!anySys) return;
    const supporters = selSys.filter((s) => (s.locations || []).includes(l.id));
    t.locations[l.id] = supporters.length
      ? { recommended: true, reason: `Anchors ${supporters.length} selected system${supporters.length > 1 ? "s" : ""}` }
      : { dim: true, reason: "No selected system fits here" };
  });

  // SYSTEMS ⇐ Locations (forward) + Crops (reverse). Recommended when it fits an active
  // constraint and conflicts with none; dimmed when it violates one.
  (kb.systems || []).forEach((s) => {
    if (!anyLoc && !anyCrop) return;
    const locOk = anyLoc ? (s.locations || []).some((l) => selLoc.includes(l)) : null;
    const serves = anyCrop ? selCrops.filter((c) => systemHostsCrop(s, c)) : null;
    const cropOk = anyCrop ? serves.length > 0 : null;
    // HARD exclusion — ground-contact systems (food forest, row crops) are not placeable on
    // rooftop/facade/indoor (directive: dynamically disable if Rooftop/Facade is selected).
    if (s.ground_contact && locOk === false) {
      t.systems[s.id] = { excluded: true, reason: "Requires on-grade ground contact — not available on the selected rooftop / facade" };
      return;
    }
    const compatible = locOk !== false && cropOk !== false;
    if (!compatible) {
      t.systems[s.id] = { dim: true, reason: cropOk === false ? "Cannot grow the selected crops" : "Not valid at the selected location" };
    } else {
      const bits = [];
      if (locOk === true) bits.push("fits the selected location");
      if (cropOk === true) bits.push(`grows ${serves.length} selected crop${serves.length > 1 ? "s" : ""}`);
      t.systems[s.id] = { recommended: true, reason: bits.join(" · ") || "compatible" };
    }
  });

  // CROPS ⇐ Systems (forward) + Resources (reverse water/nutrient loops).
  (kb.crops || []).forEach((c) => {
    if (anySys) {
      const grower = selSys.find((s) => systemHostsCrop(s, c));
      t.crops[c.id] = grower
        ? { recommended: true, reason: `Compatible with ${grower.label}` }
        : { dim: true, reason: "No selected system can grow this crop" };
    } else if (anyRes && (hasWaterLoop || hasAmendmentLoop)) {
      const loops = [hasWaterLoop && "water", hasAmendmentLoop && "nutrient"].filter(Boolean).join(" & ");
      t.crops[c.id] = { recommended: true, reason: `Selected ${loops} loop supports this crop` };
    }
  });

  // RESOURCES ⇐ Crops & Systems (forward demand): a high-water crop recommends Water loops,
  // nutrient-hungry crops recommend compost/fertilizer, energy-drawing systems recommend Energy.
  (kb.resources || []).forEach((r) => {
    const cat = r.category || "";
    const out = (r.output || "").toLowerCase();
    let rec = false, reason = "";
    if (anyCrop && (cat === "Water" || /water/i.test(out))) { rec = true; reason = "Covers selected crops' water demand"; }
    else if (anyCrop && (cat === "Amendment" || /compost|fertil|nutrient/i.test(out))) { rec = true; reason = "Feeds selected crops' nutrient demand"; }
    else if ((anySys || anyCrop) && (cat === "Energy" || cat === "Renewable Energy" || cat === "Thermal Exchange" || /energy|heat|biogas|electric|solar/i.test(out))) { rec = true; reason = "Offsets system energy demand"; }
    if (rec) t.resources[r.id] = { recommended: true, reason };
  });

  return t;
}

export function resourcesFor(kb) {
  const list = kb?.resources || [];
  return {
    inputs: list.filter((r) => r.kind === "input"),
    outputs: list.filter((r) => r.kind === "output"),
  };
}

export function systemHasArea(areas, systemId) {
  return (areas[systemId] || 0) > 0;
}

// Editable, fully-cited carbon baseline defaults (see lib/citations.js CARBON_BASELINE).
export const CARBON_BASELINE_DEFAULTS = {
  foodMilesKm: 1500,   // avg one-way distance imported fresh produce travels (editable)
  freightEF: 0.062,    // kg CO₂e / tonne-km — GLEC Framework v3.2 (2025), avg road freight
  reeferUplift: 0.30,  // refrigerated-transport energy uplift (GLEC 15–40% range)
  lossFraction: 0.254, // field + transit + distribution loss, fresh F&V — FAO 2023
  nutrientLossPct: 0.10, // nutritional-density loss over transit (transparency-only)
};

// Day/night temporal window (h) — the daylight bucket PV can offset (06:00–18:00).
const DAY_WINDOW_H = 12;

// Which systems run under artificial grow-lights (per-instance photoperiod schedule) vs. natural
// daylight (soil / outdoor / rooftop / façade — zero artificial-lighting load). Drives the per-block
// Grow-Light Schedule UI and the day/night Energy-loop split.
const LIT_SYSTEM_GROUPS = new Set(["Indoor & Controlled Environment", "Proprietary Systems"]);
export function systemNeedsGrowLights(system) {
  if (!system) return false;
  // v5: backend flags indoor-hosted / sole-source-light configurations explicitly.
  if (typeof system.needs_grow_lights === "boolean") return system.needs_grow_lights;
  if (LIT_SYSTEM_GROUPS.has(system.group)) return true;
  if (system.proprietary) return true;
  const t = `${system.group || ""} ${system.label || ""} ${system.host || ""} ${(system.match_tokens || []).join(" ")}`.toLowerCase();
  return /vertical|hydro|aero|aquapon|\bpod\b|indoor|controlled/.test(t);
}

export function computeKPIs(kb, selections, areas, weather, systemBlocks = [], occupancy = null, typologyId = null, resourceInputs = {}, synergyWeights = null, carbonBaseline = null, circWeights = null, valorizationAreaCharged = false, glazing = null) {
  if (!kb) return { total_yield: 0, water_recovery: 0, energy_offset: 0, carbon_offset: 0, spatial_efficiency: 0, synergy: 0, system_health: 0, total_area: 0, water_demand: 0, energy_demand: 0, ghg: 0, unlocked: false, energy_balance_pc: null, carbon_offset_pc: null, water_offset_pc: null, balance: null };

  const selSystems = kb.systems.filter((s) => selections.systems.includes(s.id));
  const blockSystemIds = new Set((systemBlocks || []).map((b) => b.systemId));
  let totalArea = 0;
  let yieldKg = 0;
  let waterDemand = 0;
  let energyDemand = 0;
  let ghg = 0;
  // Annualised accumulators — the Mass/Energy Balance works in absolute annual units so the
  // ring ratios (supply ÷ demand) are dimensionless and physically consistent.
  let yieldAnnual = 0, waterAnnualL = 0, energyAnnualKwh = 0, ghgAnnual = 0;
  let dliFitSum = 0, dliFitN = 0;
  // Per-instance day/night lighting accumulators — each lit block splits its operational energy into
  // lights (photoperiod-driven) vs continuous HVAC/pumps using ITS OWN schedule; daylight systems add 0.
  let lightsDayKwh = 0, lightsNightKwh = 0, contOpKwh = 0;
  let lightingDerivedKwh = 0; // transparency: indoor LED electricity derived from crop DLI
  const dliNow = weather?.dli;

  // Grid + lighting physics (v5 CONFIG, province-aware). activeGrid falls back to the BC CONFIG
  // default unless a province factor has been adopted (auto from the pinned site, or manual).
  const _CB0 = carbonBaseline || {};
  const _cfg = kb.config || {};
  const BASELINE_GRID = Number(_cfg.grid_factor_kgCO2e_per_kWh?.value) || 0.018; // grid crop.ghg was baked at
  const activeGrid = Number(_CB0.gridFactorKgPerKwh) > 0 ? Number(_CB0.gridFactorKgPerKwh) : BASELINE_GRID;
  const ledEff = Number(_cfg.led_efficacy_umol_per_J?.value) || 2.7;      // µmol/J
  const lightShare = Math.max(0.3, Math.min(1, Number(_cfg.indoor_lighting_share?.value) || 0.72));
  // Indoor LED electricity (kWh/m²·yr) to deliver a crop's target DLI: photons ÷ efficacy,
  // grossed up by the lighting share of total indoor electricity (HVAC/pumps make up the rest).
  const lightingKwhM2Yr = (dli) => {
    const d = Number(dli);
    if (!(d > 0)) return null;
    return (d / (ledEff * 3.6)) / lightShare * 365; // DLI/(µmol/J×3.6)=kWh/m²·d photons → /share ×365
  };
  // Effective operational GHG per kg = electricity × active grid + any non-electric remainder
  // (keeps footprint province-sensitive without dropping non-grid emissions baked into crop.ghg).
  const effGhgPerKg = (m) => (m.energy_use || 0) * activeGrid + Math.max(0, (m.ghg || 0) - (m.energy_use || 0) * BASELINE_GRID);
  const cropTargetDli = (c) => c ? ((c.dli_min != null && c.dli_max != null) ? (c.dli_min + c.dli_max) / 2 : (c.dli_max ?? c.dli_min ?? null)) : null;

  const cyclesPerYear = (m) => (m && m.cycle ? Math.max(1, Math.min(26, 365 / m.cycle)) : 4);

  // Grow-light context for a system/block: null = daylight (no artificial lighting load).
  const clamp01Local = (v) => Math.max(0, Math.min(1, v));
  const litCtxFor = (system, block) => {
    if (!systemNeedsGrowLights(system)) return null;
    const ph = Math.max(1, Math.min(24, Number(block?.photoperiodH) || 16));
    const share = clamp01Local((Number(block?.lightingSharePct) || 65) / 100);
    return { share, dayFrac: Math.min(ph, DAY_WINDOW_H) / ph };
  };
  const splitOperational = (opKwh, litCtx) => {
    if (litCtx) {
      const lights = opKwh * litCtx.share;
      lightsDayKwh += lights * litCtx.dayFrac;
      lightsNightKwh += lights * (1 - litCtx.dayFrac);
      contOpKwh += opKwh * (1 - litCtx.share);
    } else {
      contOpKwh += opKwh; // daylight system — operational load treated as a continuous baseload
    }
  };

  const addProduction = (m, prod, crop, litCtx, opts = {}) => {
    yieldKg += prod;
    waterDemand += (m.water_use || 0) * prod;
    // Annual yield uses the crop table's vetted annual figure (yield_annual) when present — the
    // authoritative per-zone kg/m²·yr (indoor lettuce 110, herbs 108, greenhouse tomato 47.6). Falls
    // back to per-cycle × cycles/yr only when the table has no annual value. `cyc` is the implied
    // cycle count, kept so per-cycle demand readouts stay consistent with the annual accumulators.
    const annual = (m.yield_annual != null && opts.area > 0) ? m.yield_annual * opts.area : prod * cyclesPerYear(m);
    const cyc = prod > 0 ? Math.max(1, annual / prod) : cyclesPerYear(m);
    yieldAnnual += annual;
    waterAnnualL += (m.water_use || 0) * annual;   // L/yr irrigation (ET) demand
    // Indoor sole-source lighting: derive operational electricity from the crop's target DLI and
    // LED efficacy (area-based physics) instead of the per-kg baseline. Daylight systems keep
    // the per-kg operational intensity (no artificial-lighting load).
    const litE = (opts.indoor && opts.area > 0) ? lightingKwhM2Yr(cropTargetDli(crop)) : null;
    let opKwh;
    if (litE != null) {
      opKwh = litE * opts.area;                     // kWh/yr DLI-derived indoor electricity
      energyDemand += opKwh / Math.max(1, cyc);
      ghg += (opKwh / Math.max(1, cyc)) * activeGrid;
      ghgAnnual += opKwh * activeGrid;
      lightingDerivedKwh += opKwh;
    } else {
      opKwh = (m.energy_use || 0) * annual;          // kWh/yr operational (per-kg intensity)
      energyDemand += (m.energy_use || 0) * prod;
      const eg = effGhgPerKg(m);
      ghg += eg * prod;
      ghgAnnual += eg * annual;
    }
    energyAnnualKwh += opKwh;
    splitOperational(opKwh, litCtx);
    if (crop && dliNow != null) { dliFitSum += dliFitScore(dliNow, crop.dli_min, crop.dli_max); dliFitN += 1; }
  };

  selSystems.forEach((s) => {
    const A = areas[s.id] || 0;
    totalArea += A;
    if (A <= 0) return;
    if (blockSystemIds.has(s.id)) return; // yield/demand for this system comes from its drafted blocks
    const compat = kb.crops.filter((c) => selections.crops.includes(c.id) && systemHostsCrop(s, c));
    if (!compat.length) return;
    const perCropArea = A / compat.length;
    const litCtx = litCtxFor(s, null);
    const indoor = s.crop_class === "Indoor controlled";
    compat.forEach((c) => {
      const key = cropHostKey(s, c);
      addProduction(c.systems[key] || {}, (c.systems[key]?.yield || 0) * perCropArea, c, litCtx, { indoor, area: perCropArea }); // kg per cycle
    });
  });

  // Per-block crop allocations (Section 9) — precise area × share × crop yield
  (systemBlocks || []).forEach((b) => {
    const sys = kb.systems.find((s) => s.id === b.systemId);
    const litCtx = litCtxFor(sys, b);
    // Instance-level consultant override (Excel/CSV): supersedes crop-derived math for THIS block only.
    const ov = b.overrides;
    if (ov && ov.yield != null) {
      const blockYield = (b.area || 0) * ov.yield; // consultant kg/m²/cycle
      yieldKg += blockYield;
      waterDemand += (ov.water_use || 0) * blockYield;
      energyDemand += (ov.energy_use || 0) * blockYield;
      ghg += (ov.ghg || 0) * blockYield;
      const cyc = 4;
      yieldAnnual += blockYield * cyc;
      waterAnnualL += (ov.water_use || 0) * blockYield * cyc;
      const opKwh = (ov.energy_use || 0) * blockYield * cyc;
      energyAnnualKwh += opKwh;
      splitOperational(opKwh, litCtx);
      ghgAnnual += (ov.ghg || 0) * blockYield * cyc;
      return;
    }
    const dbs = sys?.db_systems || b.dbSystems || [];
    const indoorBlk = sys?.crop_class === "Indoor controlled";
    Object.entries(b.allocations || {}).forEach(([cid, pct]) => {
      if (!pct) return;
      const c = kb.crops.find((x) => x.id === cid);
      const key = c && dbs.find((lb) => c.systems[lb]);
      if (!key) return;
      const m = c.systems[key] || {};
      const blkArea = (b.area || 0) * pct / 100;
      addProduction(m, (m.yield || 0) * blkArea, c, litCtx, { indoor: indoorBlk, area: blkArea });
    });
  });

  // ── PHASE 4 — Mass & Energy Balance ─────────────────────────────────────────────
  // No arbitrary points/caps. Ring % = true ratio of absolute annual Supply ÷ Demand.
  // Only LOCKED loops count — a selected-but-unlocked loop is still a draft and contributes nothing.
  const resObjs = kb.resources.filter((r) => selections.resources.includes(r.id) && (resourceInputs.locked || {})[r.id]);
  const isWaterLoop = (r) => r.category === "Water" || /water/i.test(r.output || "");
  const isEnergyLoop = (r) => r.category === "Energy" || /energy|heat|biogas|electric/i.test(r.output || "");
  const solarBlob = (r) => `${r.output || ""} ${r.label || ""} ${r.tech || ""}`;
  // Solar PV (electricity) and Solar Thermal (heat) are DIFFERENT technologies with different
  // efficiencies and outputs — classify + compute them independently.
  const isPvLoop = (r) => isEnergyLoop(r) && /\bpv\b|photovolt|electric/i.test(solarBlob(r));
  const isSolarThermalLoop = (r) => isEnergyLoop(r) && /solar/i.test(`${r.label || ""} ${r.tech || ""}`) && /thermal|hot water|heat/i.test(solarBlob(r));
  // Waste-heat EXCHANGE loops (ventilation / condenser) — exclude solar thermal (that's generation).
  const isHeatLoop = (r) => isEnergyLoop(r) && /heat|hrv|exchange/i.test(solarBlob(r)) && !/solar/i.test(`${r.label || ""} ${r.tech || ""}`);
  const isAmendmentLoop = (r) => r.category === "Amendment";
  const waterLoopSel = resObjs.some(isWaterLoop);
  const pvLoopSel = resObjs.some(isPvLoop);
  const solarThermalLoopSel = resObjs.some(isSolarThermalLoop);
  const heatLoopSel = resObjs.some(isHeatLoop);
  const amendmentLoopSel = resObjs.some(isAmendmentLoop);

  const t = TYPOLOGIES.find((x) => x.id === typologyId);
  const occ = Number(occupancy) > 0 ? Number(occupancy) : 0;
  const ghi = weather?.ghi || 0;        // kWh/m²·d
  // GLAZING (behind-glass daylight & passive solar gain) — from surfaces tagged "Glazed".
  // interior DLI = site DLI × area-weighted VLT (ASHS); passive solar = area × insolation × SHGC (ASHRAE Ch.15).
  const glazingArea = glazing?.area_m2 || 0;
  const glazingSolar_kWh = (glazing?.solarGainCoeff || 0) > 0 ? Math.round(ghi * 365 * glazing.solarGainCoeff) : 0;
  const interiorDli = (glazingArea > 0 && dliNow != null) ? +(dliNow * (glazing.vlt || 0)).toFixed(1) : null;
  const precip = weather?.precip || 0;  // L/m²·yr

  // Editable literature constants (defaults from the user's Waste Database + standards).
  const RUNOFF = resourceInputs.runoffCoeff ?? 0.80;   // roof runoff coefficient
  const PV_ETA = resourceInputs.pvEff ?? 0.20;         // panel efficiency (CER dataset)
  const PV_PR = resourceInputs.pvPR ?? 0.75;           // NREL performance ratio
  const ST_ETA = resourceInputs.solarThermalEff ?? 0.40; // glazed flat-plate collector efficiency
  const pvAreaM2 = Number(resourceInputs.pvAreaM2) || 0;
  const stAreaM2 = Number(resourceInputs.solarThermalAreaM2) || 0;
  const roofArea = totalArea;
  const pvArea = pvAreaM2 > 0 ? pvAreaM2 : roofArea;
  const stArea = stAreaM2 > 0 ? stAreaM2 : roofArea;

  // ── Quantified resource loops (shared with the Resources tab) ───────────────────
  // Each selected loop diverts a % of its occupant-driven stream; outputs feed the balance:
  //   water-kind → treated-water supply · energy-kind (biogas) → energy supply
  //   amendment-kind → on-site nutrient/amendment · parasitic energy → added to energy demand.
  // Per stream we cap total diverted input at the capturable amount (no >100% over-allocation).
  const streams = kb.resource_streams || {};
  const rctx = { occupancy: occ, catchmentAreaM2: roofArea, precipMmYr: precip, runoff: RUNOFF, organicKgPersonYr: organicKgPersonYr(typologyId) };
  const rq = [];
  resObjs.forEach((r) => {
    if (!r.conversion?.stream) return;
    const pct = resourceInputs.divert?.[r.id] ?? DEFAULT_DIVERT_PCT;
    const q = quantifyResource(r, pct, streams, rctx);
    if (q) rq.push(q);
  });
  const streamReq = {};
  rq.forEach((q) => {
    const s = (streamReq[q.stream] = streamReq[q.stream] || { avail: 0, req: 0 });
    s.avail = Math.max(s.avail, q.generated * q.capture);
    s.req += q.input;
  });
  let resWaterL = 0, resEnergyKwh = 0, resAmendmentKg = 0, resParasiticKwh = 0;
  rq.forEach((q) => {
    const s = streamReq[q.stream];
    const scale = s && s.req > s.avail && s.req > 0 ? s.avail / s.req : 1;
    resWaterL += q.waterL * scale;
    resEnergyKwh += q.energyKwh * scale;
    resAmendmentKg += q.amendmentKg * scale;
    resParasiticKwh += (q.parasiticKwh || 0) * scale;
  });

  // Valorization equipment footprint (v6) — informational by default. When "area charged" is ON,
  // the equipment is sited inside the growing envelope, so it displaces crop area and yield.
  let valorizationAreaM2 = 0;
  rq.forEach((q) => { if (q.spaceM2) valorizationAreaM2 += q.spaceM2; });
  let yieldDisplacedKg = 0, growArea = totalArea;
  if (valorizationAreaCharged && totalArea > 0 && valorizationAreaM2 > 0) {
    growArea = Math.max(0, totalArea - valorizationAreaM2);
    const keep = growArea / totalArea;
    yieldDisplacedKg = yieldAnnual * (1 - keep);
    yieldKg = yieldKg * keep;
    yieldAnnual = yieldAnnual * keep;
  }

  // WATER — demand = crop ET (L/yr); supply = rain + treated grey/blackwater loops.
  const rain_L = waterLoopSel ? precip * roofArea * RUNOFF : 0;
  const greywater_L = resWaterL; // driven by the quantified treatment loops (BioGill / MBR / struvite)
  const water_supply_L = rain_L + greywater_L;
  const rw = waterAnnualL > 0 ? water_supply_L / waterAnnualL : 0;

  // ENERGY — demand = operational kWh/yr (+ treatment parasitics); supply = solar PV + waste heat + biogas.
  const energyBaseKwh = energyAnnualKwh;           // lighting + HVAC (before treatment parasitics)
  energyAnnualKwh += resParasiticKwh;
  const solar_kWh = pvLoopSel ? ghi * 365 * pvArea * PV_ETA * PV_PR : 0;
  // Solar thermal: direct heat from glazed collectors (η ≈ 0.45, no PV performance ratio). Daylight only.
  const solar_thermal_kWh = solarThermalLoopSel ? Math.round(ghi * 365 * stArea * ST_ETA) : 0;
  // Thermal exchange: sum recovered kWh across LOCKED thermal loops via their per-loop config
  // (available kW × effectiveness × annual operating hours). Ventilation methods that only preheat
  // incoming OA contribute 0 to agriculture (see thermalRecovery / RECOVERY_METHODS export rule).
  const country = resourceInputs.countryName || "";
  let wasteheat_kWh = 0, thermalAvailableKw = 0;
  resObjs.filter(isHeatLoop).forEach((r) => {
    const cfg = (resourceInputs.thermal || {})[r.id] || {};
    const tr = thermalRecovery(r, cfg, country, typologyId);
    thermalAvailableKw += tr.kw;
    wasteheat_kWh += tr.recoveredKwh;
  });
  wasteheat_kWh = Math.round(wasteheat_kWh);
  const biogas_kWh = resEnergyKwh;
  // ENERGY-GRADE SEPARATION: recovered / renewable HEAT (solar thermal, ventilation & condenser
  // waste heat, passive glazing gain) is a different energy grade and CANNOT offset LED electricity.
  // It is tracked as its own thermal line (heat_recovered_kWh) and excluded from the electricity
  // offset — an indoor LED room running a heat surplus therefore reads ~0% electrical offset, not 10%.
  const heat_recovered_kWh = Math.round(solar_thermal_kWh + wasteheat_kWh + glazingSolar_kWh);
  const energy_gross_kWh = solar_kWh + biogas_kWh; // electrical generation only

  // ── Temporal (day/night) match — supply only offsets demand that occurs in the same hours ──────
  // Lighting demand is accumulated PER LIT INSTANCE above (each block's own photoperiod × share);
  // daylight systems add zero lighting load. Grow-space HVAC/pumps + treatment parasitics run 24/7.
  // PV & solar thermal are daytime-only; recovered waste heat follows the building's operating hours.
  // Biogas is DISPATCHABLE (stored, burns on demand) — allocate it to unmet demand NIGHT-FIRST so it
  // complements daytime PV/solar-thermal instead of being wasted in a flat 50/50 split.
  // Usable supply per bucket = min(supply, demand); leftover supply = surplus, leftover demand = grid.
  const contKwh = contOpKwh + resParasiticKwh;                   // HVAC/pumps/parasitics = 24/7
  const demand_day = lightsDayKwh + contKwh * 0.5;
  const demand_night = lightsNightKwh + contKwh * 0.5;
  const base_supply_day = solar_kWh;              // PV — daytime electricity only (heat excluded)
  const base_supply_night = 0;                    // no electrical night generation (biogas dispatched below)
  // Dispatch biogas: fill the night gap first, then the day gap, remainder = exportable surplus.
  const bio_to_night = Math.min(biogas_kWh, Math.max(0, demand_night - base_supply_night));
  const bio_to_day = Math.min(biogas_kWh - bio_to_night, Math.max(0, demand_day - base_supply_day));
  const bio_surplus = biogas_kWh - bio_to_night - bio_to_day;
  const supply_day = base_supply_day + bio_to_day + bio_surplus;  // leftover biogas → daytime surplus
  const supply_night = base_supply_night + bio_to_night;
  const met_day = Math.min(supply_day, demand_day);
  const met_night = Math.min(supply_night, demand_night);
  const energy_met_kWh = met_day + met_night;                     // temporally-usable supply
  const energy_supply_kWh = energyBaseKwh + resParasiticKwh > 0 ? energy_met_kWh : energy_gross_kWh;
  const eo = energyAnnualKwh > 0 ? energy_met_kWh / energyAnnualKwh : 0;
  const energy_surplus_kWh = Math.max(0, Math.round((supply_day - met_day) + (supply_night - met_night)));

  // CARBON — comparative baseline: proposed on-site yield vs the "Current Conventional System".
  // Zero fake math: every assumption is an editable, cited field (GLEC v3.2 freight EF, FAO food loss).
  // The conventional chain must OVER-produce & transport extra mass to net the same edible yield,
  // because a fraction (field + transit + distribution) is lost before it reaches the plate.
  const CB = carbonBaseline || {};
  const foodMilesKm = Math.max(0, Number(CB.foodMilesKm ?? CARBON_BASELINE_DEFAULTS.foodMilesKm));
  const freightEF = Math.max(0, Number(CB.freightEF ?? CARBON_BASELINE_DEFAULTS.freightEF)); // kg CO₂e/tonne-km
  const reeferUplift = Math.max(0, Number(CB.reeferUplift ?? CARBON_BASELINE_DEFAULTS.reeferUplift));
  const lossFraction = clamp01(Number(CB.lossFraction ?? CARBON_BASELINE_DEFAULTS.lossFraction));
  const nutrientLossPct = clamp01(Number(CB.nutrientLossPct ?? CARBON_BASELINE_DEFAULTS.nutrientLossPct));

  const grossConventionalKg = lossFraction < 1 ? yieldAnnual / (1 - lossFraction) : yieldAnnual;
  const wastedKg = grossConventionalKg - yieldAnnual;
  // Transport emissions the design avoids by growing on-site. Preferred: a cited per-kg
  // transport-carbon factor from the v5 LOC_Cities table (real freight LCA for the pinned
  // city). Fallback: the editable generic food-miles × freight-EF × reefer model.
  const citedTF = Number(CB.transportFactorKgPerKg);
  const useCited = Number.isFinite(citedTF) && citedTF > 0;
  const avoided_kg = useCited
    ? grossConventionalKg * citedTF
    : (grossConventionalKg / 1000) * foodMilesKm * freightEF * (1 + reeferUplift);
  const footprint_kg = ghgAnnual;
  const net_kg = avoided_kg - footprint_kg;
  const co = avoided_kg > 0 ? clamp01(net_kg / avoided_kg) : 0;

  // SPATIAL — light-use efficiency: how well the sited crops match the site's DLI (0..1). Reported
  // as its own KPI; NOT part of the circularity score.
  const se = dliFitN > 0 ? clamp01(dliFitSum / dliFitN) : (totalArea > 0 ? 0.5 : 0);

  const water_recovery = rw * 100;
  const energy_offset = eo * 100;
  const carbon_offset = co * 100;
  const spatial_efficiency = se * 100;

  const balance = {
    water: { demand_L: Math.round(waterAnnualL), rain_L: Math.round(rain_L), greywater_L: Math.round(greywater_L), supply_L: Math.round(water_supply_L), recovery: water_recovery, loop: waterLoopSel, surplus_L: Math.max(0, Math.round(water_supply_L - waterAnnualL)) },
    energy: { demand_kWh: Math.round(energyAnnualKwh), solar_kWh: Math.round(solar_kWh), solar_thermal_kWh: Math.round(solar_thermal_kWh), wasteheat_kWh: Math.round(wasteheat_kWh), biogas_kWh: Math.round(biogas_kWh), glazing_solar_kWh: glazingSolar_kWh, heat_recovered_kWh: heat_recovered_kWh, parasitic_kWh: Math.round(resParasiticKwh), gross_supply_kWh: Math.round(energy_gross_kWh), supply_kWh: Math.round(energy_supply_kWh), offset: energy_offset, loop: pvLoopSel || solarThermalLoopSel || heatLoopSel || biogas_kWh > 0, wasteHeatKw: Math.round(thermalAvailableKw), surplus_kWh: energy_surplus_kWh, lighting_derived_kWh: Math.round(lightingDerivedKwh), temporal: { day: { supply: Math.round(supply_day), demand: Math.round(demand_day), met: Math.round(met_day) }, night: { supply: Math.round(supply_night), demand: Math.round(demand_night), met: Math.round(met_night) } } },
    carbon: {
      avoided_kg: Math.round(avoided_kg), footprint_kg: Math.round(footprint_kg), net_kg: Math.round(net_kg), offset: carbon_offset,
      edible_yield_kg: Math.round(yieldAnnual),
      gross_conventional_kg: Math.round(grossConventionalKg),
      wasted_kg: Math.round(wastedKg),
      food_miles_km: foodMilesKm,
      freight_ef: freightEF,
      reefer_uplift: reeferUplift,
      transport_factor_kg_per_kg: useCited ? citedTF : null,
      transport_basis: useCited ? (CB.transportBasis || "Cited transport factor") : null,
      grid_factor_kg_per_kwh: activeGrid,
      grid_province: CB.gridProvince || (kb.config?.province?.value) || null,
      grid_auto: !!CB.gridAuto,
      loss_fraction: lossFraction,
      nutrient_loss_pct: nutrientLossPct,
    },
    spatial: { efficiency: spatial_efficiency, area_m2: Math.round(totalArea), interior_dli: interiorDli, glazing_vlt: glazing?.vlt || 0, glazing_shgc: glazing?.shgc || 0, glazing_area_m2: Math.round(glazingArea), glazing_solar_kWh: glazingSolar_kWh, glazing_split: glazing?.split || [] },
    yield_annual_kg: Math.round(yieldAnnual),
    amendment_loop: amendmentLoopSel,
    nutrient: { amendment_kg: Math.round(resAmendmentKg), loop: resAmendmentKg > 0 || amendmentLoopSel },
    locked_count: resObjs.length,
    weights: { ...(circWeights || CIRC_WEIGHTS) },
  };

  // CIRCULARITY SCORE — single source of truth: the loop-closure engine (Water·Energy·Nutrient·Carbon)
  // blended by the user's circWeights. `synergy`/`system_health` are kept as aliases so every consumer
  // (KPI ribbon, scenario compare, brief) shows the SAME number as the Circularity panel.
  const circScore = Math.round(circularityScore(balance, circWeights || CIRC_WEIGHTS) * 100);
  const synergy = circScore;

  return {
    total_yield: yieldAnnual,
    total_yield_per_cycle: yieldKg,
    water_recovery,
    energy_offset,
    carbon_offset,
    spatial_efficiency,
    synergy,
    system_health: synergy, // legacy alias (Synergy Score)
    total_area: totalArea,
    valorization_area_m2: valorizationAreaM2,
    valorization_area_charged: valorizationAreaCharged,
    grow_area_m2: growArea,
    yield_displaced_kg: yieldDisplacedKg,
    water_demand: waterDemand,
    energy_demand: energyDemand,
    ghg,
    balance,
    unlocked: totalArea > 0,
    energy_balance_pc: occ ? Math.round((energy_supply_kWh - energyAnnualKwh) / occ) : null,
    carbon_offset_pc: occ ? Math.round(net_kg / occ) : null,
    water_offset_pc: occ ? Math.round(water_supply_L / occ) : null,
  };
}

// P2 — Opportunity yield density (kg/m²·yr) for the currently-selected crop mix, used to project a
// rough "~X kg/yr" potential per SURFACE before systems are allocated. Surfaces are single-layer
// growing planes, so we deliberately base the density on single-layer systems (Raised-Beds,
// Greenhouse) and EXCLUDE stacked/volumetric systems (Vertical Farm, Tank) which would massively
// overstate a flat-area read. Falls back to all of a crop's systems only if it has no single-layer
// data. Each crop's annual density = yield/m²·cycle × cycles/yr, averaged across the mix.
const SINGLE_LAYER_DB_SYSTEMS = ["Raised-Beds", "Greenhouse"];
export function cropMixYieldPerM2Yr(kb, selections) {
  if (!kb || !selections?.crops?.length) return 0;
  const crops = kb.crops.filter((c) => selections.crops.includes(c.id));
  if (!crops.length) return 0;
  const annualDensity = (m) => {
    const y = m.yield || 0;
    const cyc = m.cycle ? Math.max(1, Math.min(26, 365 / m.cycle)) : 4;
    return y * cyc; // kg/m²·yr
  };
  let sum = 0, n = 0;
  crops.forEach((c) => {
    const sysEntries = Object.entries(c.systems || {});
    const singleLayer = sysEntries.filter(([lb]) => SINGLE_LAYER_DB_SYSTEMS.includes(lb));
    const use = (singleLayer.length ? singleLayer : sysEntries).map(([, m]) => annualDensity(m)).filter((v) => v > 0);
    if (use.length) { sum += use.reduce((a, b) => a + b, 0) / use.length; n += 1; }
  });
  return n > 0 ? sum / n : 0;
}

// P2 — Total structural load (kg) rolled up across all DRAFTED system blocks. Per instance:
// effective structural kg/m² (user override wins) × block area. Mirrors the Financials roll-up
// (drafted blocks only). Returns { totalKg, rows:[{id,label,loadKgM2,area,kg}] }.
export function computeStructuralLoad(kb, systemBlocks = [], systemWeightOverride = {}) {
  const rows = [];
  let totalKg = 0;
  (systemBlocks || []).forEach((b) => {
    const sys = (kb?.systems || []).find((s) => s.id === b.systemId);
    const base = sys?.baselines?.operational_weight_kgm2?.value;
    const ov = systemWeightOverride?.[b.systemId];
    const loadKgM2 = ov != null && ov !== "" ? Number(ov) : (base != null ? Number(base) : 0);
    const area = Number(b.area) || 0;
    const kg = loadKgM2 * area;
    totalKg += kg;
    rows.push({ id: b.id, label: sys?.label || b.systemId, loadKgM2, area, kg });
  });
  return { totalKg, rows };
}


function dliFitScore(dli, min, max) {
  if (dli == null) return 0.4;
  if (min == null || max == null) return 0.7;
  if (dli >= min && dli <= max) return 1;
  const d = Math.min(Math.abs(dli - min), Math.abs(dli - max));
  return Math.max(0, 1 - d / 15);
}

const clamp01 = (v) => Math.max(0, Math.min(1, v));

// ── Circularity loops — the SINGLE SOURCE OF TRUTH for the Circularity Gaps panel AND the
// Resources ring %. Each loop is scored 0..1 (capped at 1 so a surplus can't mask an open loop)
// and combined via a weighted average into the total "% of resources recovered".
// There is NO empirical basis for any particular loop weighting, so the DEFAULT is EQUAL (25%
// each) and the weights are exposed as a USER-EDITABLE assumption (see store `circWeights`).
export const CIRC_WEIGHTS = { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 };

// Named emphasis presets for the loop weights — all ASSUMPTIONS (no empirical basis), offered only
// as convenient starting points for a given site priority. Each sums to 1.0.
export const CIRC_PRESETS = {
  equal:    { label: "Equal",         weights: { water: 0.25, energy: 0.25, nutrient: 0.25, carbon: 0.25 } },
  water:    { label: "Water-scarce",  weights: { water: 0.40, energy: 0.25, nutrient: 0.15, carbon: 0.20 } },
  energy:   { label: "Energy-first",  weights: { water: 0.25, energy: 0.40, nutrient: 0.15, carbon: 0.20 } },
  carbon:   { label: "Carbon-focus",  weights: { water: 0.20, energy: 0.25, nutrient: 0.15, carbon: 0.40 } },
  nutrient: { label: "Nutrient-loop", weights: { water: 0.25, energy: 0.20, nutrient: 0.40, carbon: 0.15 } },
};

// Effective weights used for scoring: equal when the user has locked to assumption-free scoring,
// otherwise their chosen weights.
export function effectiveCircWeights(weights, locked) {
  return locked ? CIRC_WEIGHTS : (weights || CIRC_WEIGHTS);
}

export function circularityLoops(bal, weights = CIRC_WEIGHTS) {
  if (!bal) return [];
  const W = weights || CIRC_WEIGHTS;
  const rows = [];
  const wd = bal.water?.demand_L || 0, ws = bal.water?.supply_L || 0;
  if (wd > 0) {
    const frac = clamp01(ws / wd);
    rows.push({
      key: "water", label: "Water loop", weight: W.water, frac, pct: Math.round(frac * 100),
      status: ws >= wd ? "closed" : "gap",
      detail: ws >= wd ? `+${(bal.water.surplus_L || 0).toLocaleString()} L/yr surplus` : `${Math.max(0, Math.round(wd - ws)).toLocaleString()} L/yr unmet`,
    });
  }
  const ed = bal.energy?.demand_kWh || 0, es = bal.energy?.supply_kWh || 0;
  if (ed > 0) {
    const frac = clamp01(es / ed);
    const t = bal.energy?.temporal;
    const tNote = t ? ` · day ${Math.round(t.day.met).toLocaleString()}/${Math.round(t.day.demand).toLocaleString()} · night ${Math.round(t.night.met).toLocaleString()}/${Math.round(t.night.demand).toLocaleString()} kWh matched` : "";
    rows.push({
      key: "energy", label: "Energy loop", weight: W.energy, frac, pct: Math.round(frac * 100),
      status: es >= ed ? "closed" : "gap",
      detail: (es >= ed ? `+${(bal.energy.surplus_kWh || 0).toLocaleString()} kWh/yr surplus` : `${Math.max(0, Math.round(ed - es)).toLocaleString()} kWh/yr unmet (time-matched)`) + tNote,
    });
  }
  // Nutrient — always relevant: closed once a locked amendment loop recovers on-site nutrient.
  const amt = bal.nutrient?.amendment_kg || 0;
  const nutClosed = !!bal.nutrient?.loop && (amt > 0 || bal.amendment_loop);
  rows.push({
    key: "nutrient", label: "Nutrient loop", weight: W.nutrient, frac: nutClosed ? 1 : 0, pct: nutClosed ? 100 : 0,
    status: nutClosed ? "closed" : "gap",
    detail: nutClosed ? (amt > 0 ? `${amt.toLocaleString()} kg/yr amendment recovered` : "amendment loop active") : "No nutrient / amendment recovery loop",
  });
  // Carbon — always shown; offset is already 0..100 (net avoided ÷ avoidable).
  const off = clamp01((bal.carbon?.offset || 0) / 100);
  const net = bal.carbon?.net_kg || 0;
  rows.push({
    key: "carbon", label: "Carbon balance", weight: W.carbon, frac: off, pct: Math.round(off * 100),
    status: net >= 0 ? "closed" : "gap",
    detail: net >= 0 ? `net +${net.toLocaleString()} kg CO₂e/yr avoided` : `${Math.abs(net).toLocaleString()} kg CO₂e/yr over`,
  });
  return rows;
}

// Weighted average of the present loops (weights renormalized over whichever loops apply) → 0..1.
export function circularityScore(bal, weights = CIRC_WEIGHTS) {
  const rows = circularityLoops(bal, weights);
  if (!rows.length) return 0;
  const wsum = rows.reduce((a, r) => a + r.weight, 0) || 1;
  return rows.reduce((a, r) => a + r.weight * r.frac, 0) / wsum;
}

// Viability = Dependency Resolution. Each ring (0..1) reflects whether a pillar's
// choices are biologically/structurally/circularly SUPPORTED — not just counted.
// Also returns a short guidance `hint` per pillar (what's blocking 100%).
export function computeViability(kb, selections, areas, weather, kpis, weights = CIRC_WEIGHTS) {
  const scores = { locations: 0, systems: 0, crops: 0, resources: 0 };
  const hints = { locations: null, systems: null, crops: null, resources: null };
  if (!kb) return { scores, hints };

  const selSystems = kb.systems.filter((s) => selections.systems.includes(s.id));
  const selCrops = kb.crops.filter((c) => selections.crops.includes(c.id));
  const resObjs = kb.resources.filter((r) => selections.resources.includes(r.id));
  const hasWater = resObjs.some((r) => r.category === "Water" || /water/i.test(r.output || ""));
  const hasNutrient = resObjs.some((r) => r.category === "Amendment");
  const hasEnergy = resObjs.some((r) => r.category === "Energy");
  const dli = weather?.dli;
  const producing = (kpis?.total_yield || 0) > 0;

  // LOCATION — spatial/structural viability
  if (selections.locations.length === 0) {
    hints.locations = "Set a site location to anchor spatial viability.";
  } else if (selSystems.length === 0) {
    scores.locations = 0.55;
    hints.locations = "Site set — add growing systems to validate structural fit.";
  } else {
    const matched = selSystems.filter((sys) => sys.locations.some((l) => selections.locations.includes(l)));
    scores.locations = matched.length / selSystems.length;
    if (matched.length < selSystems.length) {
      const bad = selSystems.find((sys) => !sys.locations.some((l) => selections.locations.includes(l)));
      const need = bad.locations.map((l) => locationLabel(kb, l)).join(" / ");
      hints.locations = `${bad.label} is not structurally valid here; it requires ${need}.`;
    }
  }

  // SYSTEMS — infrastructural viability (area + power/water routing)
  if (selSystems.length === 0) {
    hints.systems = "Add a growing system, then allocate its area (m²).";
  } else {
    let noArea = null;
    const per = selSystems.map((sys) => {
      const areaSet = (areas[sys.id] || 0) > 0 ? 1 : 0;
      if (!areaSet && !noArea) noArea = sys;
      const infra = (hasWater ? 0.5 : 0) + (hasEnergy ? 0.5 : 0);
      return 0.6 * areaSet + 0.4 * infra;
    });
    scores.systems = per.reduce((a, b) => a + b, 0) / per.length;
    if (noArea) hints.systems = `Allocate a target area for ${noArea.label}: select a surface on the model or enter the area in m².`;
    else if (!hasWater && !hasEnergy) hints.systems = "Add an Energy or Water loop in Resources to route power and water.";
  }

  // CROPS — biological viability (light + compatible system + water)
  if (selCrops.length === 0) {
    hints.crops = "Select crops. The loop progresses as their light and system requirements are met.";
  } else {
    let worst = null;
    let worstScore = 2;
    const per = selCrops.map((c) => {
      const df = dliFitScore(dli, c.dli_min, c.dli_max);
      const sf = selSystems.some((sys) => systemHostsCrop(sys, c)) ? 1 : 0;
      const wf = hasWater ? 1 : 0.6;
      const s = 0.5 * df + 0.35 * sf + 0.15 * wf;
      if (s < worstScore) { worstScore = s; worst = { c, df, sf }; }
      return s;
    });
    scores.crops = per.reduce((a, b) => a + b, 0) / per.length;
    if (worst && scores.crops < 0.999) {
      const name = worst.c.crop;
      if (worst.sf === 0) hints.crops = `${name} requires a compatible growing system. Add one to resolve this.`;
      else if (worst.df < 0.6)
        hints.crops = dli == null
          ? `Set a site to retrieve DLI. ${name}'s viability depends on available light.`
          : `${name} requires DLI ${worst.c.dli_min}\u2013${worst.c.dli_max}; the site provides ${Math.round(dli)}. Adjust the location or add supplemental lighting.`;
      else if (!hasWater) hints.crops = `${name} is on the municipal water baseline. Add a Water loop to close it.`;
    }
  }

  // RESOURCES — the Resources ring % EQUALS the Circularity Gaps weighted total (single source of
  // truth via circularityScore). Only LOCKED loops feed the balance, so unlocked drafts never move it.
  const bal = kpis?.balance;
  const lockedCount = bal?.locked_count || 0;
  if (lockedCount === 0) {
    scores.resources = 0;
    hints.resources = producing
      ? "Your systems require water and nutrients. Add circular loops and press Done to lock them in."
      : "Add recovery loops (rainwater, compost, biodigester) and press Done to lock them in.";
  } else {
    scores.resources = circularityScore(bal, weights);
    const open = circularityLoops(bal, weights).filter((r) => r.status === "gap").map((r) => r.label.replace(/ (loop|balance)$/i, "").toLowerCase());
    const W = weights || CIRC_WEIGHTS;
    const wtxt = `water ${Math.round(W.water * 100)} · energy ${Math.round(W.energy * 100)} · nutrient ${Math.round(W.nutrient * 100)} · carbon ${Math.round(W.carbon * 100)}`;
    if (!producing) hints.resources = "Loops locked. Allocate crop area to balance real water & energy demand.";
    else if (open.length) hints.resources = `Close the ${open.join(" & ")} loop${open.length > 1 ? "s" : ""} to raise circularity (weighted: ${wtxt}).`;
    else hints.resources = "All loops closed — 100% of resources recovered.";
  }

  Object.keys(scores).forEach((k) => { scores[k] = clamp01(scores[k]); });
  const ceilings = computeCeilings(kb, selections, areas, weather, kpis, scores);
  return { scores, hints, ceilings };
}

// Absolute "Ecological Ceiling" per pillar (Potential vs Realized) — surfaced in the
// ring guide card alongside the realized viability fill.
export function computeCeilings(kb, selections, areas, weather, kpis, scores) {
  const out = { locations: null, systems: null, crops: null, resources: null };
  if (!kb) return out;
  const dli = weather?.dli;
  const selSystems = kb.systems.filter((s) => selections.systems.includes(s.id));
  const totalArea = kpis?.total_area || 0;

  // LOCATION — site microclimate ceiling
  out.locations = dli != null
    ? { label: `DLI ${Math.round(dli)} mol/m²·d`, detail: `${Math.round((scores.locations || 0) * 100)}% sited` }
    : { label: "Set a site to retrieve climate data", detail: null };

  // SYSTEMS — how much selected hardware is spatially allocated
  if (selSystems.length) {
    const withArea = selSystems.filter((s) => (areas[s.id] || 0) > 0).length;
    out.systems = { label: `${withArea}/${selSystems.length} systems allocated`, detail: totalArea > 0 ? `${Math.round(totalArea)} m² total` : "allocate m²" };
  } else {
    out.systems = { label: "Add a growing system", detail: null };
  }

  // CROPS — absolute annual yield ceiling for the allocated area & site light
  const cropPool = selSystems.length ? kb.crops.filter((c) => selSystems.some((s) => systemHostsCrop(s, c))) : kb.crops;
  let bestYield = 0;
  cropPool.forEach((c) => {
    const hostS = selSystems.find((s) => systemHostsCrop(s, c));
    const key = hostS ? cropHostKey(hostS, c) : Object.keys(c.systems || {}).find((lb) => c.systems[lb]);
    const y = (c.systems[key]?.yield || 0) * dliFitScore(dli, c.dli_min, c.dli_max);
    if (y > bestYield) bestYield = y;
  });
  if (totalArea > 0 && bestYield > 0) {
    const ceilKg = Math.round(bestYield * totalArea);
    out.crops = { label: `≈${ceilKg.toLocaleString()} kg / cycle`, detail: `${Math.round(kpis?.total_yield || 0).toLocaleString()} kg realized` };
  } else {
    out.crops = { label: "Allocate m² to reveal yield ceiling", detail: null };
  }

  // RESOURCES — closed-loop ceiling
  out.resources = { label: "100% circularity (water · nutrient · energy)", detail: `${Math.round((scores.resources || 0) * 100)}% closed` };

  return out;
}
